import * as React from 'react'

import {
  CellContext,
  ColumnDef,
  getCoreRowModel,
  RowSelectionState,
  SortingState,
  Updater,
  useReactTable,
} from '@tanstack/react-table'
import { MobXProviderContext } from 'mobx-react'

import CompanyProfilePreview from '~/client/src/shared/components/CompanyProfilePreview/CompanyProfilePreview'
import LocationIcon from '~/client/src/shared/components/LocationIcon/LocationIcon'
import {
  getLocationParentNames,
  LocationReference,
} from '~/client/src/shared/components/ObjectReference/LocationReference'
import { SensorStateBadge } from '~/client/src/shared/components/SensorStateBadge/SensorStateBadge'
import { Checkbox } from '~/client/src/shared/components/shadcn-ui/checkbox'
import UserProfilePreview from '~/client/src/shared/components/UserProfilePreview/UserProfilePreview'
import SortOrder from '~/client/src/shared/enums/SortOrder'
import { useTagsStore } from '~/client/src/shared/hooks/useTagsStore'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { getResourceGroupPaths } from '~/client/src/shared/resources/grouping/getResourceGroupPaths'
import { compareHierarchyGroupsByOptionOrder } from '~/client/src/shared/resources/grouping/hierarchyGrouping'
import {
  getLocationReferenceLabel,
  getReferencedLocation,
  getResourceLinkTarget,
} from '~/client/src/shared/resources/resourceLink'
import { IHierarchyAccessors } from '~/client/src/shared/utils/hierarchyAccessors'
import { getDisplayValue } from '~/client/src/shared/utils/propertyDisplayUtils'

import { buildGroupedRows } from '../buildGroupedRows'
import FileCell from '../Cells/FileCell'
import { useResource } from '../context'
import {
  IResourceColumn,
  IResourcePropertyConfig,
  IResourceRowData,
} from '../types'

interface IResourceCellMeta {
  propertyConfig?: IResourcePropertyConfig
  cellFormatter?: (value: any) => string
  hierarchy?: IHierarchyAccessors
  cellType:
    | 'duration'
    | 'select'
    | 'hierarchy'
    | 'checkbox'
    | 'user'
    | 'company'
    | 'file'
    | 'location'
    | 'sensorState'
    | 'icon'
    | 'color'
    | 'default'
}

const MINUTES_IN_HOUR = 60

function renderDurationValue(
  value: unknown,
  config?: IResourcePropertyConfig,
): string | null {
  if (!value) return null
  const unit = config?.unit
  const totalHours =
    unit === 'hours'
      ? Math.ceil(value as number)
      : Math.ceil((value as number) / MINUTES_IN_HOUR)
  return Localization.translator.xHours(totalHours)
}

function renderSelectValue(
  value: unknown,
  config?: IResourcePropertyConfig,
): string | null {
  if (!value || (Array.isArray(value) && value.length === 0)) return null
  return getDisplayValue(value as string | string[], config)
}

function ResourceCell({
  getValue,
  row,
  column,
}: CellContext<IResourceRowData, unknown>) {
  const { projectMembersStore, companiesStore } =
    React.useContext(MobXProviderContext)
  const tagsStore = useTagsStore()

  if (row.original.rowType === 'group') return null

  const meta = column.columnDef.meta as IResourceCellMeta | undefined
  const raw = getValue()

  if (meta?.cellType === 'duration') {
    const text = renderDurationValue(raw, meta.propertyConfig)
    return text ? <span className="tw:truncate">{text}</span> : null
  }

  if (meta?.cellType === 'select') {
    const text = renderSelectValue(raw, meta.propertyConfig)
    return text ? (
      <span className="tw:truncate" title={text}>
        {text}
      </span>
    ) : null
  }

  if (meta?.cellType === 'hierarchy') {
    if (!raw || (Array.isArray(raw) && raw.length === 0)) return null
    const values = Array.isArray(raw) ? raw : [raw]
    const parts = values.map(
      value =>
        meta.hierarchy?.getDisplayParts(String(value)) ?? {
          leaf: String(value),
          ancestorPath: '',
        },
    )
    const title = parts
      .map(({ leaf, ancestorPath }) =>
        ancestorPath ? `${leaf} ${ancestorPath}` : leaf,
      )
      .join(', ')
    return (
      <span className="tw:truncate" title={title}>
        {parts.map(({ leaf, ancestorPath }, index) => (
          <React.Fragment key={`${leaf}-${ancestorPath}-${index}`}>
            {index > 0 && ', '}
            {leaf}
            {ancestorPath && (
              <span className="tw:ml-1 tw:text-muted-foreground">
                {ancestorPath}
              </span>
            )}
          </React.Fragment>
        ))}
      </span>
    )
  }

  if (meta?.cellType === 'checkbox') {
    const isChecked = raw === true || raw === 'true'

    return (
      <div className="tw:flex tw:w-full tw:items-center tw:justify-center">
        <Checkbox checked={isChecked} disabled />
      </div>
    )
  }

  if (meta?.cellType === 'user') {
    const user = raw ? projectMembersStore?.getById(String(raw)) : null

    // Clickable by default: UsernameFromUid opens the global user card, guards itself on
    // the user resolving, and stops propagation so the row's own click never fires.
    return user ? <UserProfilePreview user={user} /> : null
  }

  if (meta?.cellType === 'company') {
    const company = raw ? companiesStore?.getCompanyById(String(raw)) : null

    return company ? (
      <CompanyProfilePreview
        company={company}
        isMenuHidden={true}
        isPhoneHidden={true}
      />
    ) : null
  }

  if (meta?.cellType === 'location') {
    // Deleted ones included: a child still names its deleted parent, shown marked.
    const location = raw ? getReferencedLocation(String(raw), tagsStore) : null

    if (!location) return null

    const label = getLocationReferenceLabel(location.id, tagsStore)

    return (
      <LocationReference
        iconName={location.iconName}
        color={location.color}
        variant="plain"
        size="dense"
        parentNames={getLocationParentNames(
          location,
          tagsStore.tagStoreByTagTypeMap,
        )}
        resourceCode={location.stxResourceId}
      >
        <span title={label}>{label}</span>
      </LocationReference>
    )
  }

  if (meta?.cellType === 'file') {
    return <FileCell value={raw} />
  }

  if (meta?.cellType === 'sensorState') {
    return <SensorStateBadge value={raw} />
  }

  if (meta?.cellType === 'icon') {
    if (!raw) return null
    const iconName = String(raw)
    // Read the sibling color off the full record so the glyph is tinted to match.
    const color = row.original.record?.color || undefined
    return <LocationIcon iconName={iconName} color={color} size={22} />
  }

  if (meta?.cellType === 'color') {
    if (!raw) return null
    const hex = String(raw)
    // Just the color swatch — no hex text. Title exposes the value on hover.
    return (
      <span
        className="tw:inline-block tw:h-5 tw:w-8 tw:rounded-sm tw:border tw:border-solid tw:border-border"
        style={{ backgroundColor: hex }}
        title={hex}
      />
    )
  }

  const value = meta?.cellFormatter ? meta.cellFormatter(raw) : raw
  return value != null ? (
    <span className="tw:truncate">{String(value)}</span>
  ) : null
}

function determineCellType(
  config?: IResourcePropertyConfig,
  dataKey?: string,
  cellHint?: IResourceCellMeta['cellType'],
): IResourceCellMeta['cellType'] {
  // A per-field hint (icon glyph / color swatch) wins over the type-based rules,
  // since icon/color are plain text propertyTypes that would otherwise render text.
  if (cellHint) return cellHint
  if (dataKey === 'data.sensor_state') return 'sensorState'
  if (config?.unit === 'minutes' || config?.unit === 'hours') return 'duration'
  if (config?.propertyType === 'checkbox') return 'checkbox'
  if (config?.propertyType === 'user') return 'user'
  if (config?.propertyType === 'company') return 'company'
  if (config?.propertyType === 'file') return 'file'
  if (config?.propertyType === 'resource_link') {
    const target = getResourceLinkTarget(config)
    if (target) return target.cellType
  }
  if (config?.propertyType === 'hierarchy') return 'hierarchy'
  if (config?.options) return 'select'
  return 'default'
}

function buildColumnDef(col: IResourceColumn): ColumnDef<IResourceRowData> {
  return {
    id: col.dataKey,
    header: col.label ?? '',
    accessorFn: (row: IResourceRowData) =>
      row.record ? col.accessorFn(row.record) : undefined,
    cell: ResourceCell,
    meta: {
      propertyConfig: col.propertyConfig,
      cellFormatter: col.cellFormatter,
      hierarchy: col.hierarchy,
      cellType: determineCellType(
        col.propertyConfig,
        col.dataKey,
        col.cellType,
      ),
    } satisfies IResourceCellMeta,
    size: col.width ?? 200,
    enableResizing: true,
    enableSorting: true,
  }
}

function toColumnSizing(widths: Map<string, number>): Record<string, number> {
  const result: Record<string, number> = {}
  widths.forEach((v, k) => {
    result[k] = v
  })
  return result
}

function toColumnVisibility(
  columns: Pick<IResourceColumn, 'dataKey' | 'isHidden'>[],
): Record<string, boolean> {
  return columns.reduce<Record<string, boolean>>((result, column) => {
    result[column.dataKey] = !column.isHidden
    return result
  }, {})
}

function toSortingState(sorting: {
  key: string
  order: SortOrder
}): SortingState {
  return [{ id: sorting.key, desc: sorting.order === SortOrder.DESC }]
}

function createDisplayRecord(
  record: any,
  id: string,
  mergedData?: Record<string, any>,
  mergedRecord?: any,
): IResourceRowData {
  const root = record.root || {}
  const data = mergedData ?? { ...record.data, ...(record.rollups || {}) }
  const recordWithMergedData = mergedRecord ?? { ...record, data, root }

  return {
    id,
    recordId: record.id,
    rowType: 'record',
    name: record.name,
    stxResourceId: record.stxResourceId,
    data,
    root,
    record: recordWithMergedData,
  }
}

export function useResourceTableAdapter() {
  const {
    state: {
      filteredRecords,
      grouping,
      selectedIds,
      sorting,
      collapsedCategories,
    },
    actions: { setSelection, setSorting },
    meta: {
      configuredColumns,
      columns: allColumns,
      columnWidths,
      onColumnResize,
      fieldProjector,
      fieldCatalog,
    },
  } = useResource()

  const displayColumns = configuredColumns?.length
    ? configuredColumns
    : allColumns

  const data = React.useMemo(() => {
    // Cache merged data + record-with-merged-data by record id so the same
    // record appearing in multiple groups doesn't re-spread its property bag.
    const mergedDataByRecordId = new Map<string, Record<string, any>>()
    const recordByRecordId = new Map<string, any>()
    const buildDisplayRecord = (record: any, id: string): IResourceRowData => {
      let mergedData = mergedDataByRecordId.get(record.id)
      if (!mergedData) {
        mergedData = { ...record.data, ...(record.rollups || {}) }
        mergedDataByRecordId.set(record.id, mergedData)
      }
      let mergedRecord = recordByRecordId.get(record.id)
      if (!mergedRecord) {
        mergedRecord = {
          ...record,
          data: mergedData,
          root: record.root || {},
        }
        recordByRecordId.set(record.id, mergedRecord)
      }
      return createDisplayRecord(record, id, mergedData, mergedRecord)
    }

    if (!grouping) {
      return filteredRecords.map(record =>
        buildDisplayRecord(record, record.id),
      )
    }

    const hierarchy = fieldCatalog.getField(grouping)?.hierarchy

    return buildGroupedRows({
      records: filteredRecords,
      fieldKey: grouping,
      getRecordGroupPaths: record =>
        getResourceGroupPaths(
          record,
          grouping,
          hierarchy,
          fieldProjector,
          Localization.translator.unassigned,
        ),
      getRecordId: record => record.id,
      collapsed: collapsedCategories,
      buildGroupRow: (group, rowId): IResourceRowData => ({
        id: rowId,
        rowType: 'group',
        groupKey: group.value,
        groupLabel: group.label,
        groupRecordIds: group.recordIds,
        groupPath: group.path,
        depth: group.depth,
        isGroupCollapsed: group.isCollapsed,
        name: group.label,
        data: {},
        root: {},
      }),
      buildLeafRow: buildDisplayRecord,
      compareGroups:
        hierarchy &&
        ((a, b) => compareHierarchyGroupsByOptionOrder(a, b, hierarchy)),
    })
  }, [
    filteredRecords,
    grouping,
    fieldCatalog,
    fieldProjector,
    collapsedCategories,
  ])

  const recordIds = React.useMemo(
    () => Array.from(new Set(filteredRecords.map(record => record.id))),
    [filteredRecords],
  )

  const allVisibleSelected = React.useMemo(
    () => recordIds.length > 0 && recordIds.every(id => selectedIds.has(id)),
    [recordIds, selectedIds],
  )

  const someVisibleSelected = React.useMemo(
    () => recordIds.some(id => selectedIds.has(id)),
    [recordIds, selectedIds],
  )

  const columnDefs = React.useMemo((): ColumnDef<IResourceRowData>[] => {
    const defs: ColumnDef<IResourceRowData>[] = [
      {
        id: 'select',
        header: () => (
          <label
            className="tw:flex tw:h-full tw:w-full tw:items-center tw:justify-center"
            onClick={event => event.stopPropagation()}
            onMouseDown={event => event.stopPropagation()}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.stopPropagation()
              }
            }}
          >
            <Checkbox
              checked={allVisibleSelected}
              indeterminate={someVisibleSelected && !allVisibleSelected}
              aria-label="Select all rows"
              data-testid="resource-select-all-checkbox"
              onCheckedChange={() => {
                const next = new Set(selectedIds)
                if (allVisibleSelected) {
                  recordIds.forEach(id => next.delete(id))
                } else {
                  recordIds.forEach(id => next.add(id))
                }
                setSelection(next)
              }}
            />
          </label>
        ),
        cell: ({ row }) => {
          if (row.original.rowType === 'group') return null
          const recordId = row.original.recordId
          if (!recordId) return null

          return (
            <label
              className="tw:flex tw:h-full tw:w-full tw:items-center tw:justify-center"
              onClick={event => event.stopPropagation()}
              onMouseDown={event => event.stopPropagation()}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.stopPropagation()
                }
              }}
            >
              <Checkbox
                checked={selectedIds.has(recordId)}
                aria-label={`Select row ${row.index + 1}`}
                data-testid="resource-row-checkbox"
                onCheckedChange={() => {
                  const next = new Set(selectedIds)
                  if (next.has(recordId)) {
                    next.delete(recordId)
                  } else {
                    next.add(recordId)
                  }
                  setSelection(next)
                }}
              />
            </label>
          )
        },
        enableSorting: false,
        enableResizing: false,
        enablePinning: true,
        size: 40,
        meta: { align: 'center' },
      },
    ]

    for (const col of displayColumns) {
      if (col.dataKey === 'checkbox') continue
      defs.push(buildColumnDef(col))
    }
    return defs
  }, [
    allVisibleSelected,
    displayColumns,
    recordIds,
    selectedIds,
    setSelection,
    someVisibleSelected,
  ])

  const fixedColumnIds = React.useMemo(
    () => [
      'select',
      ...displayColumns
        .filter(
          col => col.dataKey !== 'checkbox' && col.isFixed && !col.isHidden,
        )
        .map(col => col.dataKey),
    ],
    [displayColumns],
  )

  const columnSizing = React.useMemo(
    () => toColumnSizing(columnWidths),
    [columnWidths],
  )
  const columnVisibility = React.useMemo(
    () => toColumnVisibility(displayColumns),
    [displayColumns],
  )
  const sortingState = React.useMemo(() => toSortingState(sorting), [sorting])

  const handleSortingChange = React.useCallback(
    (updater: Updater<SortingState>) => {
      const next =
        typeof updater === 'function' ? updater(sortingState) : updater
      if (next.length > 0) {
        const { id, desc } = next[0]
        setSorting(id, desc ? SortOrder.DESC : SortOrder.ASC)
      }
    },
    [sortingState, setSorting],
  )

  const table = useReactTable<IResourceRowData>({
    data,
    columns: columnDefs,
    getRowId: row => row.id,
    state: {
      columnPinning: { left: fixedColumnIds },
      columnSizing,
      columnVisibility,
      sorting: sortingState,
      rowSelection: {} as RowSelectionState,
    },
    onSortingChange: handleSortingChange,
    onColumnSizingChange: updater => {
      const next =
        typeof updater === 'function' ? updater(columnSizing) : updater
      Object.entries(next).forEach(([key, width]) => {
        if (columnSizing[key] !== width) {
          onColumnResize(key, width)
        }
      })
    },
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: 'onChange',
    manualSorting: true,
    enableSortingRemoval: false,
  })

  return { table }
}
